import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { PriceOverrideUploadResponse } from 'api/types/priceOverrideTypes'
import {
  FetchOrgSettingsResponse,
  OrgSetting,
  RefreshPricesRequest,
  UpdatePricingMethodRequest,
  UpdatePricingOverrideRequest,
  UpdatePricingSettingRequest,
} from 'api/types/settingsApiTypes'
import useApi from 'api/useApi'
import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

import { useOptimisticPendingChanges } from './gc-dags/useOptimisticPendingChanges'
import { QueryKey } from './queryKeys'

export const useOrgSetting = <T extends OrgSetting>(
  setting: T,
  queryOptions?: { enabled: boolean }
) => {
  const { update_org_settings, fetch_org_settings } = useApi()
  const orgSettingsQueryKey = useOrgQueryKey(QueryKey.OrgSettings)

  const { hasPermission } = useUser()
  const userHasPermission = hasPermission(Permission.SettingOrgRead)
  const enabled = (queryOptions?.enabled ?? true) && userHasPermission

  const {
    data: settings,
    refetch,
    isLoading,
  } = useQuery(orgSettingsQueryKey, fetch_org_settings, {
    enabled,
  })

  return {
    value: settings?.[setting],
    isLoading,
    update: (value: FetchOrgSettingsResponse[T]) =>
      update_org_settings(setting, value).then(() => refetch()),
  }
}

// Exposes the backend-supplied option lists for select-based org settings. Reuses
// the org settings query key so it shares the same cached fetch as useOrgSetting.
export const useOrgSettingOptions = () => {
  const { fetch_org_settings } = useApi()
  const orgSettingsQueryKey = useOrgQueryKey(QueryKey.OrgSettings)

  const { hasPermission } = useUser()
  const enabled = hasPermission(Permission.SettingOrgRead)

  const { data: settings, isLoading } = useQuery(orgSettingsQueryKey, fetch_org_settings, {
    enabled,
  })

  return { options: settings?._options, isLoading }
}

export const useGetPricingSettings = (queryOptions?: { enabled: boolean }) => {
  const { fetch_org_pricing_setting } = useApi()
  const enabled = queryOptions?.enabled ?? true

  const pricingOverridesQueryKey = useOrgQueryKey(QueryKey.PricingSettings)
  const { isLoading, data } = useQuery(pricingOverridesQueryKey, fetch_org_pricing_setting, {
    enabled,
  })

  return {
    isLoading,
    pricingSettings: data?.result,
    data,
  }
}

export const useUpdatePricingSettings = () => {
  const { update_org_pricing_setting } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    async (req: UpdatePricingSettingRequest) => {
      return update_org_pricing_setting(req)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PricingSettings])
        setPendingChangeFlag('pricing_update')
      },
    }
  )
}

export const useUpdatePricingMethod = () => {
  const { update_org_pricing_setting } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    async (req: UpdatePricingMethodRequest) => {
      return update_org_pricing_setting(req)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PricingSettings])
        setPendingChangeFlag('pricing_update')
      },
    }
  )
}

export const useGetPricingOverrides = () => {
  const { fetch_org_pricing_overrides } = useApi()
  const pricingOverridesQueryKey = useOrgQueryKey(QueryKey.PricingOverrides)

  return useQuery(pricingOverridesQueryKey, fetch_org_pricing_overrides)
}

export const useUpsertPricingOverride = () => {
  const { upsert_org_pricing_override } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    async ({ asset, provider }: UpdatePricingOverrideRequest) => {
      return upsert_org_pricing_override(asset, provider)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PricingOverrides])
        setPendingChangeFlag('pricing_update')
      },
    }
  )
}

export const useDeletePricingOverride = () => {
  const { delete_org_pricing_override } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    async (asset: string) => {
      return delete_org_pricing_override(asset)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PricingOverrides])
        setPendingChangeFlag('pricing_update')
      },
    }
  )
}

export const useRefreshPrices = () => {
  const { refresh_prices } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    async (request: RefreshPricesRequest) => {
      return refresh_prices(request)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PricingSettings])
        void queryClient.invalidateQueries([QueryKey.PricingOverrides])
        setPendingChangeFlag('pricing_refresh_request')
      },
    }
  )
}

export const useUploadPriceOverride = () => {
  const { upload_price_override } = useApi()
  const queryClient = useQueryClient()

  return useMutation<PriceOverrideUploadResponse, unknown, FormData>(
    async (formData: FormData) => {
      return upload_price_override(formData)
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([QueryKey.PriceOverrideUploads])
      },
    }
  )
}

export const usePriceOverrideUploads = () => {
  const { fetch_price_override_uploads } = useApi()
  const queryKey = useOrgQueryKey(QueryKey.PriceOverrideUploads)

  return useQuery(queryKey, fetch_price_override_uploads)
}
